import type { TripType } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/hooks/search-inputs-manager'
import { ExpandLess } from '@mui/icons-material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Button, Menu, MenuItem } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

interface ITripTypeSelectorProps {
    tripType: TripType
    onTripTypeChange?: (tripType: TripType) => void
    active: boolean
    onActivate: () => void
    onClose: () => void
}

export const TripTypeSelector: React.FC<ITripTypeSelectorProps> = ({
    tripType,
    onTripTypeChange,
    active,
    onActivate,
    onClose
}) => {
    const t = useTranslations('common')
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const labelMap: Record<TripType, string> = {
        oneway: t('oneWay'),
        roundtrip: t('roundTrip'),
        multicity: t('multicity')
    }

    const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (active) {
            setAnchorEl(null)
            onClose()
        } else {
            setAnchorEl(event.currentTarget)
            onActivate()
        }
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
        onClose()
    }

    const handleTripTypeSelect = (value: TripType) => {
        onTripTypeChange?.(value)
        setAnchorEl(null)
        onClose()
    }

    // Set anchor element when component becomes active from external trigger
    useEffect(() => {
        if (active && !anchorEl) {
            const button = document.querySelector('[data-testid="triptype-button"]') as HTMLElement
            if (button) {
                setAnchorEl(button)
            }
        } else if (!active) {
            setAnchorEl(null)
        }
    }, [active, anchorEl])

    const options: TripType[] = ['oneway', 'roundtrip', 'multicity']

    return (
        <Box sx={{ pr: 0.5 }}>
            <Button
                data-testid="triptype-button"
                variant="text"
                onClick={handleButtonClick}
                sx={{
                    minWidth: 'max-content',
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                        backgroundColor: 'transparent'
                    }
                }}
                endIcon={active ? <ExpandLess /> : <ExpandMoreIcon />}
            >
                {labelMap[tripType]}
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={active && Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{ top: 8 }}
            >
                {options.map(option => (
                    <MenuItem
                        key={option}
                        selected={tripType === option}
                        onClick={() => handleTripTypeSelect(option)}
                    >
                        {labelMap[option]}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    )
}
